import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    ElementRef,
    HostListener,
    Inject,
    input,
    InputSignal,
    OnDestroy,
    output,
    ViewChild,
} from "@angular/core";
import {
    Tournament,
    TournamentModeEnum,
    TournamentStatusEnum,
} from "@Front-end/seed/__generated__";
import {
    TournamentDirective,
    TournamentsDirective,
} from "@Front-end/seed/modules/v2/tournament";
import {
    ButtonV3KindDirective,
    ButtonV3PaletteDirective,
    ButtonV3SizeDirective,
} from "@Front-end/ui/modules/button-v3";
import { IconComponent } from "@Front-end/ui/modules/icon";
import { noScrollClass } from "@Front-end/ui/modules/modal";
import { TranslateModule } from "@ngx-translate/core";
import { FilterGameTournamentsPipe } from "../../pipes/filter-game-tournaments.pipe";
import { GetPoolPrizePipe } from "../../pipes/get-pool-prize.pipe";
import { LeaderboardComponent } from "../leaderboard/leaderboard.component";
import { TournamentCardComponent } from "../tournament-card/tournament-card.component";
import { TournamentDescriptionComponent } from "../tournament-description/tournament-description.component";
import { TournamentsGamesListComponent } from "../tournaments-games-list/tournaments-games-list.component";

@Component({
    selector: "ls-tournaments-sidebar",
    templateUrl: "./tournaments-sidebar.component.html",
    styleUrls: ["./tournaments-sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        ButtonV3KindDirective,
        ButtonV3PaletteDirective,
        ButtonV3SizeDirective,
        IconComponent,
        TournamentCardComponent,
        TournamentsDirective,
        TournamentDirective,
        TournamentsGamesListComponent,
        TournamentDescriptionComponent,
        GetPoolPrizePipe,
        FilterGameTournamentsPipe,
        LeaderboardComponent,
    ],
    // eslint-disable-next-line @angular-eslint/no-host-metadata-property
    host: {
        "class": "tournaments-sidebar",
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "[class._visible]": "isOpen",
    },
})
export class TournamentsSidebarComponent implements OnDestroy {
    constructor(
        private cdr: ChangeDetectorRef,
        @Inject(DOCUMENT) private document: Document,
    ) {
    }

    @ViewChild("sidebarBody", { static: true, read: ElementRef })
    public sidebarBody!: ElementRef<Element>;

    public lsOnCloseTournamentsSidebar = output<void>();
    public lsOnGameClick = output<void>();

    public gameId: InputSignal<number[]> = input<number[]>([]);

    public isOpen: boolean = false;
    public statusActive: string = TournamentStatusEnum.Running;
    public isTournamentDetailsSelected = false;
    public tournamentDetailsSelected: Tournament | null = null;
    public tournamentMode = TournamentModeEnum;

    @HostListener("click", ["$event"])
    public onClick(event: Event): void {
        this.clickOutsideSidebar(event);
    }

    public onGameClick(): void {
        this.isOpen = false;
        this.toggleScroll();
        this.lsOnGameClick.emit();
    }

    public toggleSidebar(): void {
        this.isOpen = !this.isOpen;
        this.toggleScroll();
        this.cdr.markForCheck();
    }

    public closeSidebar(event?: Event): void {
        event?.stopPropagation();
        this.isOpen = false;
        this.toggleScroll();
        this.lsOnCloseTournamentsSidebar.emit();
        this.cdr.markForCheck();
    }

    public toggleScroll(): void {
        if (this.isOpen) {
            this.scrollPage(false);
        }

        if (!this.isOpen) {
            this.scrollPage(true);
        }
    }

    public openTournamentDetails(card: Tournament): void {
        this.tournamentDetailsSelected = card;
        this.isTournamentDetailsSelected = true;
        this.scrollToUp();
    }

    public closeTournamentDetails(event: Event): void {
        event.stopPropagation();
        this.isTournamentDetailsSelected = false;
        this.tournamentDetailsSelected = null;
    }

    public ngOnDestroy(): void {
        this.scrollPage(true);
    }

    private scrollPage(isScrolling: boolean): void {
        const body: HTMLElement = this.document.body;

        if (isScrolling) {
            body.classList.remove(noScrollClass);
        }

        if (!isScrolling) {
            body.classList.add(noScrollClass);
        }
    }

    private scrollToUp(): void {
        if (this.sidebarBody && this.sidebarBody.nativeElement) {
            this.sidebarBody.nativeElement.scrollIntoView({ block: "start" });
        }
    }

    private clickOutsideSidebar(event: Event): void {
        const clickOutSidebar = !(event.target as HTMLElement).closest(".tournaments-sidebar__bar");

        if (clickOutSidebar) {
            this.closeSidebar(event);
        }
    }
}
